/** Manages the active drag-and-drop state and provides metadata for the item currently being dragged. */
// @ts-check
import { useState } from 'react';

const FALLBACK_DRAG_HEIGHT_PX = 30;

/**
 * A hook to manage the state of a dragged task item for dnd-kit.
 *
 * This hook is now 'dumb' and only responsible for tracking the active
 * item during a drag operation.
 *
 * @param {Task[]} tasks - The list of tasks from which to find the active task.
 * @returns {{
 *   activeItem: DndData | null,
 *   handleDragStart: (event: import('@dnd-kit/core').DragStartEvent) => void,
 *   handleDragCancel: () => void
 * }}
 */
export const useTaskDragAndDrop = (tasks) => {
  const [activeItem, setActiveItem] = useState(null);

  /** @param {import('@dnd-kit/core').DragStartEvent} event */
  const handleDragStart = (event) => {
    const { active } = event;
    const task =
      active.data?.current?.task || tasks.find((t) => t.id === active.id);
    if (task) {
      setActiveItem({
        ...(active.data.current || {}),
        id: active.id,
        task: task,
        height: active.rect.current.initial?.height || FALLBACK_DRAG_HEIGHT_PX,
      });
    }
  };

  const handleDragCancel = () => {
    setActiveItem(null);
  };

  // `handleDragEnd` is intentionally removed.
  // The logic now resides in the page components (`OrganizePage`, `PlanPage`)
  // to ensure sorting is based on the global `allTasks` list.

  return { activeItem, handleDragStart, handleDragCancel };
};
